import React from 'react'
import styled from '@emotion/styled'
import RoadImage from 'src/assets/images/map-mock-blur.png'
import SatelliteImage from 'src/assets/images/map-satellite-blur.jpg'
import Image from 'next/image'
import { Typography } from '@mui/material'
import { Colors, Shapes, Levels } from 'src/components/theme'
import { IconButton } from '@mui/material'
import { HelpOutline as HelpCircle } from '@emotion-icons/material/HelpOutline'
import { InsightCardWrapperProps } from 'src/components/insights/common/cards/card'
import { HelpTooltip } from 'src/components/help-tooltip'
import { MapComponent, MapComponentProps } from 'src/components/map/mapbox'
import { MapStyle } from 'src/components/map-new/types'
import { INSIGHT_WIDGET_PRINTABLE_CLASS } from 'src/atoms/report-insight'
import { useElement } from 'src/components/report/contexts/element'

type InsightCardMapProps = MapComponentProps & {
  id?: string
  title?: string
  top?: React.ReactNode
  topRight?: React.ReactNode
  bottom?: React.ReactNode
  bottomHelp?: boolean
  bottomNoPadding?: boolean
  tooltipFile?: string
  height?: number
  useFlex?: boolean
}

const DEFAULT_HEIGHT = 477

export const InsightCardMapWrapper = styled.div<InsightCardWrapperProps>`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${Colors.bgWhite};
  margin: 0;
  border: 1px solid ${Colors.border};
  padding: 0;
  border-radius: ${(props: InsightCardWrapperProps) =>
    props.isFooterOpen ? `${Shapes.borderRadius}px ${Shapes.borderRadius}px 0 0` : Shapes.borderRadius}px;
  overflow: hidden;
`

const InsightCardMapHeader = styled.div`
  position: absolute;
  top: ${Shapes.spacing[3]}px;
  left: ${Shapes.spacing[3]}px;
  background: ${Colors.bgWhite};
  border: 1px solid ${Colors.border};
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[3]}px;
  border-radius: ${Shapes.borderRadius}px;
  z-index: ${Levels.insightsMapHeader};
  display: flex;
`

const InsightCardMapHeaderTitle = styled.div`
  margin-right: ${Shapes.spacing[3]}px;
`

export const InsightCardMapRightBtn = styled(IconButton)`
  width: 12px;
  height: 12px;
  padding: 0;
`

export const InsightCardMapHelpCircleIcon = styled(HelpCircle)`
  width: 12px;
  height: 12px;
  color: ${Colors.textLight};
`

const InsightCardMapContent = styled.div`
  position: relative;
  display: flex;
  height: 100%;
`

const InsightCardMapContentTopRight = styled.div`
  position: absolute;
  top: ${Shapes.spacing[3]}px;
  right: ${Shapes.spacing[3]}px;
  z-index: ${Levels.mapTypeToggle};
`

const InsightCardMapTop = styled.div`
  display: flex;
  border-bottom: 1px solid ${Colors.border};
  padding: ${Shapes.spacing[4]}px;
  align-items: center;
  overflow: hidden;
`

type InsightCardMapBottomProps = {
  bottomNoPadding?: boolean
  useFlex: boolean
}

const InsightCardMapBottom = styled.div<InsightCardMapBottomProps>`
  display: ${(props) => (props.useFlex ? 'flex' : 'block')};
  border-top: 1px solid ${Colors.border};
  padding: ${(props) => (props.bottomNoPadding ? 0 : `${Shapes.spacing[2]}px`)};
  align-items: center;
  justify-content: center;
  overflow: hidden;
`
type ContentProps = {
  useFlex: boolean
}

const InsightCardMapBottomContent = styled.div<ContentProps>`
  display: ${(props) => (props.useFlex ? 'flex' : 'block')};
  justify-content: center;
  align-items: center;
`

export const InsightCardMap: React.FC<InsightCardMapProps> = (props) => {
  const { id, title, bottom, bottomNoPadding, bottomHelp, tooltipFile, top, topRight, height, useFlex } = props
  const element = useElement()

  const preview = element?.status.preview
  const mapHeight = height || DEFAULT_HEIGHT

  return (
    <InsightCardMapWrapper
      id={id}
      className={`insight-card-map-wrapper ${INSIGHT_WIDGET_PRINTABLE_CLASS}`}
      style={{ height: 'auto' }}>
      {top && <InsightCardMapTop>{top}</InsightCardMapTop>}
      {title && (
        <InsightCardMapHeader>
          <InsightCardMapHeaderTitle>
            <Typography variant="text2" color={Colors.textBlack}>
              {title}
            </Typography>
          </InsightCardMapHeaderTitle>
          {tooltipFile && (
            <HelpTooltip file={tooltipFile} placement="bottom" immediate>
              <InsightCardMapRightBtn aria-label="Help">
                <InsightCardMapHelpCircleIcon />
              </InsightCardMapRightBtn>
            </HelpTooltip>
          )}
        </InsightCardMapHeader>
      )}
      <InsightCardMapContent style={{ height: mapHeight }}>
        {topRight && <InsightCardMapContentTopRight>{topRight}</InsightCardMapContentTopRight>}
        {preview ? (
          <Image src={props.mapStyle === MapStyle.STREETS ? RoadImage : SatelliteImage} alt="map" layout="fill" />
        ) : (
          <MapComponent {...props} controls={true} />
        )}
      </InsightCardMapContent>
      {bottom && (
        <InsightCardMapBottom useFlex={Boolean(useFlex)} bottomNoPadding={bottomNoPadding}>
          <InsightCardMapBottomContent useFlex={Boolean(useFlex)}>{bottom}</InsightCardMapBottomContent>
          {bottomHelp && tooltipFile ? (
            <HelpTooltip file={tooltipFile} placement="bottom" immediate>
              <InsightCardMapRightBtn aria-label="Help">
                <InsightCardMapHelpCircleIcon />
              </InsightCardMapRightBtn>
            </HelpTooltip>
          ) : null}
        </InsightCardMapBottom>
      )}
    </InsightCardMapWrapper>
  )
}
